<template>
  <article
    v-if="isAssistant"
    class="flex items-start gap-x-4 text-gray-30"
  >
    <div class="overflow-hidden w-full">
      <div class="flex flex-col gap-y-2 bg-gray-96 p-6 rounded-lg">
        <VueMarkdownIt
          class="prose prose-sm text-sm break-words"
          :source="content"
          :plugins="mdPlugins"
        />
      </div>
      <div
        v-if="!message.hideReactions && !isAnswering"
        class="flex flex-wrap items-center justify-end gap-x-3 my-4"
      >
        <p
          v-if="speech"
          class="text-right text-2xs"
        >
          {{ $t('warnings.aiGeneratedVoice') }}
        </p>
        <button
          v-tooltip="{ content: $t('controls.speech'), theme: 'tooltip-light' }"
          type="button"
          class="btn btn-primary-light"
          @click="handleSpeech(content)"
        >
          <IconsAudioVolumeStop
            v-if="isPlaying"
            class="size-4"
          />
          <IconsReload
            v-else-if="isSpeechLoading"
            class="size-4 animate-spin"
          />
          <IconsAudioVolumeNew
            v-else
            class="size-4"
          />
        </button>
        <button
          v-tooltip="{ content: $t('controls.copy'), theme: 'tooltip-light' }"
          type="button"
          class="btn btn-primary-light"
          @click="handleCopy"
        >
          <IconsCopy class="w-4 h-4" />
        </button>
        <button
          v-tooltip="{ content: $t('controls.goodResponse'), theme: 'tooltip-light' }"
          type="button"
          class="btn btn-primary-light"
          :class="{ 'disabled:text-dlubal-blue': message.liked }"
          @click="handleLike(message.id)"
          :disabled="message.liked || message.disliked"
        >
          <IconsThumbUp class="w-4 h-4" />
        </button>
        <button
          v-tooltip="{ content: $t('controls.badResponse'), theme: 'tooltip-light' }"
          type="button"
          class="btn btn-primary-light"
          :class="{ 'disabled:text-dlubal-blue': message.disliked }"
          @click="handleDislike(message.id)"
          :disabled="message.liked || message.disliked"
        >
          <IconsThumbDown class="w-4 h-4" />
        </button>
      </div>
      <ChatCarousel
        v-if="enableCitations > 0 && message.citations"
        :citations="message.citations"
        class="mt-5"
      />
    </div>
  </article>
  <article
    v-if="isUser"
    class="flex items-center justify-end group text-gray-30"
  >
    <button
      v-tooltip="{ content: $t('controls.editMessage'), theme: 'tooltip-light' }"
      type="button"
      class="btn btn-primary-light mr-3.5 transition-opacity opacity-100 md:opacity-0 duration-300 ease-in-out group-hover:opacity-100"
      @click="handleEdit(content)"
    >
      <IconsPen class="w-4 h-4" />
    </button>
    <div class="text-sm px-6 pt-[13px] pb-[11px] rounded-lg bg-blue-light w-[90%] transition">
      {{ content }}
    </div>
  </article>
</template>

<script setup lang="ts">
import { FeedbackApiResponseReaction, type BaseApiMessage } from '@/types/api'
import { useCustomGPT } from '@/stores/customGPT'
import { computed, onMounted, ref, toRefs } from 'vue'
import { storeToRefs } from 'pinia'
import ChatCarousel from '@/components/chat/ChatCarousel.vue'
import { VueMarkdownIt } from '@f3ve/vue-markdown-it'
import mila from 'markdown-it-link-attributes'
import MarkdownItKatex from '@vscode/markdown-it-katex'
import 'katex/dist/katex.min.css'
import { useConversationStore } from '@/stores/conversationStore'
import { useTextToSpeech } from '@/composables/useTextToSpeech'
import { useAudio } from '@/composables/useAudio'
import Clarity from '@microsoft/clarity'

const conversationStore = useConversationStore()
const { focus } = conversationStore
const { promptMessage, isAnswering } = storeToRefs(conversationStore)
const { doTts } = useTextToSpeech()

const props = defineProps<{
  message: BaseApiMessage
}>()

const speech = ref<HTMLAudioElement | null>(null)
const isSpeechLoading = ref(false)
const { isPlaying, togglePlayStop } = useAudio(speech, true)

const mdPlugins = [
  [
    mila,
    {
      attrs: {
        target: '_blank',
        rel: 'noopener'
      }
    }
  ],
  MarkdownItKatex
]

const { role, content } = toRefs(props.message)

const isUser = computed(() => {
  return role.value === 'user'
})

const isAssistant = computed(() => {
  return role.value === 'assistant'
})

const customGPTstore = useCustomGPT()
const { sendMessageResponseFeedback } = customGPTstore

const enableCitations = parseInt(import.meta.env.VITE_SETTINGS_ENABLE_CITATIONS)

const handleCopy = () => {
  Clarity.event('clickedCopyMessage')
  navigator.clipboard.writeText(content.value)
}
const handleLike = (id: string) => {
  sendMessageResponseFeedback({ promptId: id, reaction: FeedbackApiResponseReaction.Like })
  Clarity.event('clickedLikeMessage')
  props.message.liked = true
}
const handleDislike = (id: string) => {
  sendMessageResponseFeedback({ promptId: id, reaction: FeedbackApiResponseReaction.Dislike })
  Clarity.event('clickedDislikeMessage')
  props.message.disliked = true
}
const handleEdit = (message: string) => {
  Clarity.event('clickedEditMessage')
  promptMessage.value = message
  focus()
}

const handleSpeech = async (message: string) => {
  if (isSpeechLoading.value) {
    return
  }
  Clarity.event('clickedSpeechMessage')
  if (speech.value === null) {
    isSpeechLoading.value = true
    speech.value = await doTts(message)
    isSpeechLoading.value = false
    return
  }

  togglePlayStop()
}

onMounted(() => {
  props.message.liked = false
  props.message.disliked = false
})
</script>
